/**
 * Request Deduplication Utility
 *
 * Prevents duplicate API calls to the same endpoint while allowing concurrent
 * requests to share the same promise.
 */

import { log } from './DebugLogger'

interface CacheEntry<T = unknown> {
  promise: Promise<T>
  timestamp: number
  requestors: Set<string>
}

export interface ExecuteOptions {
  ttl?: number
  requestor?: string
}

export interface DeduplicatorStatsEntry {
  key: string
  age: number
  requestors: string[]
}

export interface DeduplicatorStats {
  size: number
  maxSize: number
  entries: DeduplicatorStatsEntry[]
}

class RequestDeduplicator {
  private _cache = new Map<string, CacheEntry>()
  private _defaultTtl = 5 * 60 * 1000
  private _maxCacheSize = 100
  private _debug = false

  setDebugMode(enabled: boolean): void {
    this._debug = enabled
  }

  private _generateKey(url: string, params: unknown = null, method: string = 'GET'): string {
    if (method === 'GET' && params) {
      const paramString = typeof params === 'string' ? params : JSON.stringify(params)
      return `${method}:${url}?${paramString}`
    }
    return `${method}:${url}`
  }

  async execute<T = unknown>(key: string, requestFn: () => Promise<T>, options: ExecuteOptions = {}): Promise<T> {
    const { ttl = this._defaultTtl, requestor = 'unknown' } = options

    if (this._cache.has(key)) {
      const entry = this._cache.get(key)! as CacheEntry<T>

      const now = Date.now()
      if (now - entry.timestamp < ttl) {
        log('PAYROLL_REFACTOR', 'CACHE_METRICS', 'DEDUPLICATOR', 'DEDUPE_HIT', {
          key,
          requestor,
          pendingCount: this._cache.size
        })

        if (this._debug) {
          log('RequestDeduplicator', 'execute', `Cache HIT - Reusing existing promise`, {
            key,
            requestor,
            existingRequestors: Array.from(entry.requestors),
            age: now - entry.timestamp
          })
        }

        entry.requestors.add(requestor)

        return entry.promise
      } else {
        if (this._debug) {
          log('RequestDeduplicator', 'execute', `Cache EXPIRED - Removing stale entry`, {
            key,
            age: now - entry.timestamp,
            ttl
          })
        }
        this._cache.delete(key)
      }
    }

    log('PAYROLL_REFACTOR', 'CACHE_METRICS', 'DEDUPLICATOR', 'DEDUPE_MISS', {
      key,
      requestor,
      pendingCount: this._cache.size
    })

    if (this._debug) {
      log('RequestDeduplicator', 'execute', `Cache MISS - Creating new request`, {
        key,
        requestor,
        cacheSize: this._cache.size
      })
    }

    if (this._cache.size >= this._maxCacheSize) {
      this._evictOldest()
    }

    const requestorSet = new Set<string>([requestor])
    const timestamp = Date.now()

    const promise = requestFn().finally(() => {
      setTimeout(() => {
        this._cache.delete(key)
      }, ttl)
    })

    const entry: CacheEntry<T> = {
      promise,
      timestamp,
      requestors: requestorSet
    }

    this._cache.set(key, entry as CacheEntry)

    return promise
  }

  private _evictOldest(): void {
    let oldestKey: string | null = null
    let oldestTimestamp = Infinity

    for (const [key, entry] of this._cache.entries()) {
      if (entry.timestamp < oldestTimestamp) {
        oldestTimestamp = entry.timestamp
        oldestKey = key
      }
    }

    if (oldestKey) {
      if (this._debug) {
        log('RequestDeduplicator', '_evictOldest', `Evicting oldest entry`, {
          key: oldestKey,
          age: Date.now() - oldestTimestamp
        })
      }
      this._cache.delete(oldestKey)
    }
  }

  invalidate(key: string): void {
    if (this._cache.delete(key)) {
      if (this._debug) {
        log('RequestDeduplicator', 'invalidate', `Cache entry invalidated`, { key })
      }
    }
  }

  invalidatePattern(pattern: string): void {
    const regex = new RegExp(pattern)
    let count = 0

    for (const key of this._cache.keys()) {
      if (regex.test(key)) {
        this._cache.delete(key)
        count++
      }
    }

    if (this._debug && count > 0) {
      log('RequestDeduplicator', 'invalidatePattern', `Invalidated ${count} entries`, { pattern })
    }
  }

  clear(): void {
    const size = this._cache.size
    this._cache.clear()

    if (this._debug) {
      log('RequestDeduplicator', 'clear', `Cleared all cache entries`, { count: size })
    }
  }

  getStats(): DeduplicatorStats {
    const entries = Array.from(this._cache.entries())

    return {
      size: this._cache.size,
      maxSize: this._maxCacheSize,
      entries: entries.map(([key, entry]) => ({
        key,
        age: Date.now() - entry.timestamp,
        requestors: Array.from(entry.requestors)
      }))
    }
  }
}

const requestDeduplicator = new RequestDeduplicator()

interface DeduplicatorWindow {
  __requestDeduplicatorDebug?: () => void
  __requestDeduplicatorStats?: () => DeduplicatorStats
  __requestDeduplicatorClear?: () => void
}

;(window as unknown as DeduplicatorWindow).__requestDeduplicatorDebug = () => {
  requestDeduplicator.setDebugMode(true)
  log('RequestDeduplicator', '__requestDeduplicatorDebug', 'Debug mode enabled')
}

;(window as unknown as DeduplicatorWindow).__requestDeduplicatorStats = () => {
  return requestDeduplicator.getStats()
}

;(window as unknown as DeduplicatorWindow).__requestDeduplicatorClear = () => {
  requestDeduplicator.clear()
}

export default requestDeduplicator
export { RequestDeduplicator }
